import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addToCart as apiAddToCart } from '../../services/cartApi';
import { getTranslatedProduct } from '../../utils/translationUtils';
import { useCurrency } from '../../context/CurrencyContext';

const colorMap = {
  Black: '#000000',
  Grey: '#9CA3AF',
  Gray: '#9CA3AF',
  White: '#FFFFFF',
  Yellow: '#FDE047',
  Blue: '#1D4ED8',
  Green: '#16A34A',
  'Mint Green': '#86EFAC',
  Brown: '#92400E',
  Pink: '#F472B6',
  Red: '#EF4444',
  'Bi-Color':
    'linear-gradient(135deg, #ff0000, #ffa500, #ffff00, #00ff00, #0000ff, #4b0082, #ee82ee)',
  Champagne: '#F5DEB3',
  Silver: '#D1D5DB',
  Purple: '#6B21A8',
  Navy: '#1E3A8A',
  Orange: '#F97316',
  Beige: '#F5F5DC',
  'Navy Blue': '#1E3A8A', // Keep for backward compatibility if needed, but Navy is the standard now
};

const defaultSizeOptions = ['S', 'M', 'L', 'XL', '2XL', '3XL'];

const Hero = ({ product, loading, error }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const [activeColor, setActiveColor] = useState('black');
  const [activeSize, setActiveSize] = useState('M');
  const [faqOpen, setFaqOpen] = useState('details');

  const price = useMemo(() => {
    if (product && product.price != null) {
      const val = Number(product.price);
      if (!Number.isNaN(val)) return formatPrice(val);
    }
    return formatPrice(390);
  }, [product, formatPrice]);

  const mainImage =
    (product?.images && product.images[0]) || '/assets/main-product.webp';
  const title =
    getTranslatedProduct(product, 'name', i18n.language) || 'Jacket';
  const desc =
    getTranslatedProduct(product, 'description', i18n.language) ||
    'It is a long established fact that a reader will be distracted by the readable conteIt is a long established fact that a reader will be distracted by the readable conteIt is a long established fact that a reader will be distracted by the readable conte';
  // const isWorkwear = (product?.category || '').toLowerCase() === 'workwear';

  const colorsFromProduct = useMemo(() => {
    if (product?.workwear?.colors && product.workwear.colors.length > 0) {
      return product.workwear.colors;
    }
    return Array.isArray(product?.colors) ? product.colors : [];
  }, [product?.workwear?.colors, product?.colors]);

  const sizesFromProduct = useMemo(() => {
    if (product?.workwear?.sizes && product.workwear.sizes.length > 0) {
      return product.workwear.sizes;
    }
    return Array.isArray(product?.sizes) ? product.sizes : [];
  }, [product?.workwear?.sizes, product?.sizes]);


  const colorLabels = colorsFromProduct.length
    ? colorsFromProduct
    : Object.keys(colorMap);

  const sizeLabels = sizesFromProduct.length
    ? sizesFromProduct
    : defaultSizeOptions;


  const colorOptions = colorLabels.map((label) => {
    const key = String(label).toLowerCase().replace(/\s+/g, '-');
    const value = colorMap[label] || '#111827';
    return { key, value, label };
  });

  const sizeOptions = sizeLabels
    .map((s) => String(s).toUpperCase())
    .map((v) => (v === 'XXL' ? '2XL' : v === 'XXXL' ? '3XL' : v));

  // Ensure defaults follow the product-provided options (only when product changes)
  React.useEffect(() => {
    if (colorLabels.length > 0) {
      const first = String(colorLabels[0])
        .toLowerCase()
        .replace(/\s+/g, '-');
      setActiveColor(first);
    }
    if (sizeOptions.length > 0) {
      setActiveSize(sizeOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, product?._id]);
  return (
    <div className="px-4 sm:px-6 xl:px-8 w-full">
      <div className="mt-24 sm:mt-[100px] flex items-start gap-2.5 md:gap-6 justify-between md:flex-row flex-col max-w-[1300px] w-full mx-auto">
        <div className="md:sticky w-full top-[100px] bg-gray-50 rounded-[16px] p-1">
          <div className="w-full relative rounded-[16px] overflow-hidden max-w-[758px] w-full bg-gray-50">
            {loading ? (
              <div className="w-full h-[400px] lg:h-[700px] flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <img
                src={mainImage}
                alt={title}
                className="w-full h-[400px] lg:h-[700px] object-contain bg-gray-50 rounded-[16px]"
              />
            )}
            {/* <button
                        aria-label="Play video"
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/80 text-white grid place-items-center hover:scale-105 transition"
                    >
                        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.5 8.90192C17.5 9.47141 17.5 10.8783 16.5 11.4478L3 19.174C2 19.7435 0.749999 19.0239 0.749999 17.8979L0.75 2.45179C0.75 1.32579 2 0.606165 3 1.17565L16.5 8.90192Z" fill="white" />
                        </svg>
                    </button> */}
          </div>
        </div>

        {/* Details */}
        <div className="lg:pt-4 sm:max-w-[353px] lg:max-w-[468px] w-full">
          <h1 className="text-[#1D1D1F] text-[20px] leading-[33px] font-medium">
            {title}
          </h1>
          <p className="text-[#1D1D1F] text-[14px] leading-[14px] font-medium mt-2">
            {price}
          </p>

          <p className="text-[#676767] text-[13px] leading-[23px] font-medium mt-4 sm:mt-7 md:max-w-[353px]">
            {desc}
          </p>

          <div className="flex items-center text-[#000000] text-[14px] sm:text-[16px] leading-[22px] sm:leading-[26px] inter font-semibold mt-5">
            {t('product.ecoFriendly')}
          </div>


          <div className="mt-2">
            <p className="text-[14px] leading-[23px] text-[#1D1D1F] font-normal">
              <span className="font-semibold">{t('product.color')}:</span>{' '}
              {colorOptions.find((c) => c.key === activeColor)?.label ||
                activeColor.charAt(0).toUpperCase() + activeColor.slice(1)}
            </p>
            <div className="flex items-center gap-x-2 gap-y-2 flex-wrap mt-2.5">
              {colorOptions.map((color) => (
                <button
                  key={color.key}
                  type="button"
                  aria-label={`Select ${color.label || color.key}`}
                  onClick={() => setActiveColor(color.key)}
                  className={`min-w-10 h-10 rounded-full border cursor-pointer flex items-center justify-center transition ${activeColor === color.key
                    ? 'border-2 border-black'
                    : 'border border-transparent hover:border-gray-300'
                    }`}
                >
                  {String(color.value).startsWith('linear-gradient') ? (
                    <span
                      className="block w-8 h-8 rounded-full"
                      style={{ backgroundImage: color.value }}
                    />
                  ) : (
                    <span
                      className="block w-8 h-8 rounded-full border border-gray-300"
                      style={{ background: color.value }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-[22px]">
            <p className="text-[14px] leading-[23px] text-[#111827] font-normal">
              <span className="font-semibold">{t('product.size')}:</span>{' '}
              {t(`product.sizes.${activeSize}`, activeSize)}
            </p>
            <div className="flex items-center gap-2.5 mt-2.5">
              {sizeOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => setActiveSize(s)}
                  className={`w-11 h-11 rounded-full border text-[16px] font-semibold transition ${activeSize === s
                    ? 'bg-[#000000] text-[#FFFFFF] border-[#000000]'
                    : 'bg-[#E4E4E4] text-[#8B8B8B] border-[#E4E4E4] hover:bg-[#E5E7EB]'
                    }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>


          <button
            className="mt-[24px] w-full sm:w-[353px] h-[50px] bg-black text-white text-[16px] leading-[20px] rounded-[5px] font-medium hover:bg-[#111827]"
            onClick={() => {
              const token =
                typeof localStorage !== 'undefined'
                  ? localStorage.getItem('token')
                  : null;
              const productId = product?.id || product?._id;
              if (token && productId) {
                apiAddToCart({
                  productId,
                  quantity: 1,
                  color: activeColor,
                  size: activeSize,
                })
                  .then(() => navigate(`/${i18n.language}/cart`))
                  .catch((e) => {
                    console.error(
                      'Add to cart failed, falling back to local cart:',
                      e
                    );
                    const item = {
                      id: productId,
                      name: title,
                      price: product?.price ?? 390.0,
                      quantity: 1,
                      image: mainImage,
                      color: activeColor,
                      size: activeSize,
                    };
                    const existing = JSON.parse(
                      localStorage.getItem('cart') || '[]'
                    );
                    const idx = existing.findIndex(
                      (p) =>
                        p.id === item.id &&
                        p.color === item.color &&
                        p.size === item.size
                    );
                    if (idx >= 0) existing[idx].quantity += 1;
                    else existing.push(item);
                    localStorage.setItem('cart', JSON.stringify(existing));
                    navigate(`/${i18n.language}/cart`);
                  });
              } else {
                const item = {
                  id: productId || 'product-unknown',
                  name: title,
                  price: product?.price ?? 390.0,
                  quantity: 1,
                  image: mainImage,
                  color: activeColor,
                  size: activeSize,
                };
                const existing = JSON.parse(
                  localStorage.getItem('cart') || '[]'
                );
                const idx = existing.findIndex(
                  (p) =>
                    p.id === item.id &&
                    p.color === item.color &&
                    p.size === item.size
                );
                if (idx >= 0) existing[idx].quantity += 1;
                else existing.push(item);
                localStorage.setItem('cart', JSON.stringify(existing));
                navigate(`/${i18n.language}/cart`);
              }
            }}
          >
            {t('product.addToCart')}
          </button>

          <div className="mt-[26px]">
            <div className="">
              {[
                {
                  key: 'details',
                  title: t('product.details'),
                  content: (
                    <ul className="border-b border-[#EEEEEE] list-disc pl-9 text-[#676767] text-[13px] leading-[23px] font-medium py-2.5">
                      {(() => {
                        // Try to get translated features first
                        const translatedFeatures =
                          product?.translations?.features?.[i18n.language];

                        // Get features from translated array or original array or fallback
                        const features =
                          translatedFeatures &&
                            Array.isArray(translatedFeatures)
                            ? translatedFeatures
                            : Array.isArray(product?.features) &&
                              product.features.length > 0
                              ? product.features
                              : [
                                t('productFeatures.breathableFabric'),
                                t('productFeatures.reinforcedKnees'),
                                t('productFeatures.industrialWashing'),
                              ];

                        return features.map((f, i) => <li key={i}>{f}</li>);
                      })()}
                    </ul>
                  ),
                },
                {
                  key: 'measurements',
                  title: t('product.measurements'),
                  content: (
                    <div className="border-b border-[#EEEEEE] py-2.5 pl-5 text-[#676767] text-[13px] leading-[23px] font-medium">
                      {product?.specifications?.Measurements ||
                        t('product.sizeFit')}
                    </div>
                  ),
                },
                {
                  key: 'shipping',
                  title: t('product.shipping'),
                  content: (
                    <div className="border-b border-[#EEEEEE] py-2.5 pl-5 text-[#676767] text-[13px] leading-[23px] font-medium">
                      {product?.specifications?.Shipping ||
                        t('product.shippingText')}
                    </div>
                  ),
                },
                {
                  key: 'specs',
                  title: t('product.specifications'),
                  content: (
                    <div className="border-b border-[#EEEEEE] py-2.5 pl-5 text-[#676767] text-[13px] leading-[23px] font-medium">
                      {(() => {
                        // Translation logic for specifications
                        const translatedSpecs =
                          product?.translations?.specifications?.[i18n.language];
                        const specs =
                          translatedSpecs && Array.isArray(translatedSpecs)
                            ? translatedSpecs
                            : Array.isArray(product?.specifications) &&
                              product.specifications.length > 0
                              ? product.specifications
                              : [];

                        return specs.length > 0 ? (
                          <ul className="list-disc pl-4">
                            {specs.map((spec, i) => (
                              <li key={i}>{spec}</li>
                            ))}
                          </ul>
                        ) : (
                          <span>{t('product.noSpecs')}</span>
                        );
                      })()}
                    </div>
                  ),
                },
              ].map((item) => {
                const open = faqOpen === item.key;
                return (
                  <div key={item.key}>
                    <button
                      type="button"
                      onClick={() => setFaqOpen(item.key)}
                      className="w-full flex items-center gap-3 py-3.5 px-3.5 border-t border-b first:border-t-0 border-[#EEEEEE]"
                      aria-expanded={open}
                      aria-controls={`faq-${item.key}`}
                    >
                      <span className="">
                        {open ? (
                          <svg
                            width="19"
                            height="17"
                            viewBox="0 0 19 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              width="18"
                              height="16"
                              transform="translate(0.584961 0.598267)"
                              fill="white"
                            />
                            <path
                              d="M13.3138 9.60885V8.58765C13.3138 8.44814 13.2636 8.32677 13.1632 8.22353C13.0627 8.12029 12.9427 8.06867 12.8032 8.06867H6.62577C6.48626 8.06867 6.36628 8.12029 6.26583 8.22353C6.16539 8.32677 6.11516 8.44814 6.11516 8.58765V9.60885C6.11516 9.74836 6.16539 9.86973 6.26583 9.97297C6.36628 10.0762 6.48626 10.1278 6.62577 10.1278H12.8032C12.9427 10.1278 13.0627 10.0762 13.1632 9.97297C13.2636 9.86973 13.3138 9.74836 13.3138 9.60885ZM15.0591 5.99697C15.0591 5.99697 15.1965 6.23343 15.4713 6.70637C15.7462 7.1793 15.8836 7.9766 15.8836 9.09825C15.8836 10.2199 15.6087 11.2537 15.0591 12.1995C14.5094 13.1454 13.7616 13.8932 12.8158 14.4428C11.8699 14.9925 10.8361 15.2673 9.71449 15.2673C8.59284 15.2673 7.55908 14.9925 6.61321 14.4428C5.66734 13.8932 4.91957 13.1454 4.36991 12.1995C3.82024 11.2537 3.54541 10.2199 3.54541 9.09825C3.54541 7.9766 3.82024 6.94284 4.36991 5.99697C4.91957 5.0511 5.66734 4.30333 6.61321 3.75366C7.55908 3.204 8.59284 2.92916 9.71449 2.92916C10.8361 2.92916 11.8699 3.204 12.8158 3.75366C13.7616 4.30333 14.5094 5.0511 15.0591 5.99697Z"
                              fill="black"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="19"
                            height="17"
                            viewBox="0 0 19 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.3138 9.60971V8.5885C13.3138 8.44899 13.2636 8.32762 13.1632 8.22438C13.0627 8.12115 12.9427 8.06953 12.8032 8.06953H10.7441V6.01038C10.7441 5.87087 10.6925 5.75089 10.5892 5.65044C10.486 5.55 10.3646 5.49977 10.2251 5.49977H9.20389C9.06438 5.49977 8.94301 5.55 8.83977 5.65044C8.73654 5.75089 8.68492 5.87087 8.68492 6.01038V8.06953H6.62577C6.48626 8.06953 6.36628 8.12115 6.26583 8.22438C6.16539 8.32762 6.11516 8.44899 6.11516 8.5885V9.60971C6.11516 9.74922 6.16539 9.87059 6.26583 9.97382C6.36628 10.0771 6.48626 10.1287 6.62577 10.1287H8.68492V12.1878C8.68492 12.3273 8.73654 12.4473 8.83977 12.5478C8.94301 12.6482 9.06438 12.6984 9.20389 12.6984H10.2251C10.3646 12.6984 10.486 12.6482 10.5892 12.5478C10.6925 12.4473 10.7441 12.3273 10.7441 12.1878V10.1287H12.8032C12.9427 10.1287 13.0627 10.0771 13.1632 9.97382C13.2636 9.87059 13.3138 9.74922 13.3138 9.60971ZM15.0591 5.99782C15.0591 5.99782 15.1965 6.23429 15.4713 6.70722C15.7462 7.18016 15.8836 7.97745 15.8836 9.0991C15.8836 10.2208 15.6087 11.2545 15.0591 12.2004C14.5094 13.1463 13.7616 13.894 12.8158 14.4437C11.8699 14.9934 10.8361 15.2682 9.71449 15.2682C8.59284 15.2682 7.55908 14.9934 6.61321 14.4437C5.66734 13.894 4.91957 13.1463 4.36991 12.2004C3.82024 11.2545 3.54541 10.2208 3.54541 9.0991C3.54541 7.97745 3.82024 6.94369 4.36991 5.99782C4.91957 5.05195 5.66734 4.30418 6.61321 3.75452C7.55908 3.20485 8.59284 2.93002 9.71449 2.93002C10.8361 2.93002 11.8699 3.20485 12.8158 3.75452C13.7616 4.30418 14.5094 5.05195 15.0591 5.99782Z"
                              fill="#888888"
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        className={`text-[14px] leading-[15px] font-medium ${open ? 'text-[#000000]' : 'text-[#000000A6]'}`}
                      >
                        {item.title}
                      </span>
                    </button>
                    <div id={`px-3.5 faq-${item.key}`}>
                      {open && item.content}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
